import { gql } from '@apollo/client';
import {
  Box,
  Button,
  chakra,
  FormControl,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  useClipboard,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { isEmpty, isNil } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import { EmptyViewExternalLink } from '~components/EmptyView';
import { SubscriptionCustomersTableV2 } from '~components/organization/SubscriptionCustomersTableV2';
import { SubscriptionCustomersTableV2CustomerFragment as CustomerFragment } from '~components/organization/SubscriptionCustomersTableV2/__generated__/SubscriptionCustomersTable.graphql';
import { useSubscriptionCustomersTableV2 } from '~components/organization/SubscriptionCustomersTableV2/useSubscriptionCustomersTable';
import { getSubscriptionTagVariant } from '~components/organization/SubscriptionsTableV2/useSubscriptionsTable';
import { CopyIcon, EditIcon } from '~components/ui/icons';
import { SubscriptionTagV2 } from '~components/ui/SubscriptionTagV2';
import { VerticalTabContent } from '~components/ui/VerticalTabs';
import useGoBack from '~components/useGoBack';
import { fromError } from '~utils/errors';
import { useFeatureFlag } from '~utils/features';
import { isDefined } from '~utils/types';
import {
  SubscriptionEditPageV2_SubscriptionFragment as SubscriptionFragment,
  useEditSubscriptionV2Mutation,
  useUpdateSubscriptionNameV2Mutation,
  useWaveSubscriptionEditPageV2OrganizationQuery,
} from './__generated__/[id].graphql';

const schema = z.object({
  id: z.string().uuid(),
  customerIds: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;

export function OrganizationSubscriptionEditPageV2() {
  const { subscriptionId } = useParams();
  const toast = useToast();
  const handleGoBack = useGoBack();
  const [updateSubscription] = useEditSubscriptionV2Mutation();

  const { data, loading, error, refetch } = useWaveSubscriptionEditPageV2OrganizationQuery();

  const subscription = data?.organization.waveSubscriptions?.find(
    (subscription) => subscription.id === subscriptionId,
  );
  const customers = data?.organization.customers;

  const updateForm = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      customerIds: subscription?.customers.map(({ id }) => id) ?? [],
    },
  });

  const { isSubmitting, isDirty, isValid } = updateForm.formState;
  const canSubmit = !isSubmitting && isValid && isDirty;

  const handleUpdateSubscription = async ({ id, customerIds }: FormValues) => {
    try {
      const { data: updateData } = await updateSubscription({
        variables: {
          input: {
            subscriptionId: id,
            customerIds,
          },
        },
      });

      if (!isDefined(updateData) || !isDefined(updateData.updateSubscription.id)) {
        throw new Error('Update subscription failed');
      }

      toast({
        status: 'success',
        title: t('subscriptionSettingsUpdated'),
      });

      await refetch();
    } catch (error) {
      toast({
        status: 'error',
        title: t('updateSubscriptionErr'),
        description: fromError(error, 'UpdateSubscription', {
          NOT_ALLOWED_TO_ASSIGN_ROLE: t('updateSubscriptionErrDesc'),
          MUST_HAVE_ADMIN: t('updateSubscriptionErrDesc1'),
          ROLES_EMPTY: t('updateSubscriptionErrDesc2'),
          UNAUTHORIZED: t('updateSubscriptionErrDesc3'),
          NOT_ALLOWED_ROLE_REMOVAL: t('updateSubscriptionErrDesc4'),
        }),
      });
      updateForm.reset();
    }
  };

  // This page loads data asynchronously, so we need to "reset" the form when the data is available
  // to fill out the default values
  useEffect(() => {
    if (loading === false) {
      updateForm.reset({
        id: subscription?.id,
        customerIds: subscription?.customers.map(({ id }) => id) ?? [],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want this to run once, when the page is loaded
  }, [loading]);

  return (
    <VerticalTabContent
      title={t('editSubscription')}
      error={error}
      isLoading={loading}
      data={subscription}
      onGoBack={handleGoBack}
      hasStickyHeader={true}
      action={{
        label: t('applyChanges'),
        disabled: !canSubmit,
        isLoading: isSubmitting,
        onClick: updateForm.handleSubmit(handleUpdateSubscription),
      }}
    >
      {(subscription) => (
        <VStack gap="10" alignItems="stretch">
          <SubscriptionDetailGridV2 subscription={subscription} onNameUpdated={refetch} />
          {subscription.assignable && (
            <EditSubscriptionFormV2
              subscription={subscription}
              customers={customers ?? []}
              form={updateForm}
            />
          )}
          <SubscriptionDetailManageV2 subscription={subscription} />
        </VStack>
      )}
    </VerticalTabContent>
  );
}

type SubscriptionDetailGridV2Props = {
  subscription: SubscriptionFragment;
  onNameUpdated: () => void;
};

export function SubscriptionDetailGridV2({
  subscription,
  onNameUpdated,
}: SubscriptionDetailGridV2Props) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onCopy, hasCopied } = useClipboard(subscription.id);
  const displayName = subscription.userGivenName ?? subscription.name;

  const tagVariant = getSubscriptionTagVariant(subscription);

  return (
    <>
      <Box border="1px solid" borderColor="gray.100" borderRadius="base" paddingX="4" paddingY="4">
        {/* Header row: Name + Edit icon | horizontal line | Badge */}
        <HStack
          spacing="3"
          paddingBottom="3"
          marginBottom="3"
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <HStack spacing="2" flexShrink={0}>
            <chakra.span fontWeight="bold" fontSize="md">
              {displayName}
            </chakra.span>
            <IconButton
              aria-label={t('editSubscriptionName')}
              icon={<EditIcon />}
              size="xs"
              variant="ghost"
              onClick={onOpen}
            />
          </HStack>
          <Box flex="1" />
          <Box flexShrink={0}>
            <SubscriptionTagV2 variant={tagVariant} />
          </Box>
        </HStack>

        {/* Detail labels */}
        <Grid templateColumns="2fr 1fr 1fr 1fr" gap={1}>
          <GridItem>
            <chakra.span fontSize="sm" color="gray.500">
              {t('subscriptionIdColumn', { defaultValue: 'Subscription ID' })}
            </chakra.span>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm" color="gray.500">
              {t('usage')}
            </chakra.span>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm" color="gray.500">
              {t('validUntil')}
            </chakra.span>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm" color="gray.500">
              {t('customers')}
            </chakra.span>
          </GridItem>

          {/* Detail values */}
          <GridItem>
            <HStack spacing="1">
              <Tooltip label={subscription.id} placement="top">
                <chakra.span fontSize="sm" cursor="default" isTruncated maxW="200px">
                  {subscription.id}
                </chakra.span>
              </Tooltip>
              <Tooltip label={hasCopied ? 'Copied!' : 'Copy ID'} closeOnClick={false}>
                <IconButton
                  aria-label="Copy subscription ID"
                  icon={<CopyIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={onCopy}
                />
              </Tooltip>
            </HStack>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm">
              {subscription.usage.current}/{subscription.usage.max}
            </chakra.span>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm">
              {DateTime.fromISO(subscription.validUntil).toFormat('dd/MM/yyyy')}
            </chakra.span>
          </GridItem>
          <GridItem>
            <chakra.span fontSize="sm">{subscription.customers.length}</chakra.span>
          </GridItem>
        </Grid>
      </Box>

      <EditSubscriptionNameModal
        isOpen={isOpen}
        onClose={onClose}
        subscriptionId={subscription.id}
        currentName={displayName}
        onNameUpdated={onNameUpdated}
      />
    </>
  );
}

type EditSubscriptionNameModalProps = {
  isOpen: boolean;
  onClose: () => void;
  subscriptionId: string;
  currentName: string;
  onNameUpdated: () => void;
};

function EditSubscriptionNameModal({
  isOpen,
  onClose,
  subscriptionId,
  currentName,
  onNameUpdated,
}: EditSubscriptionNameModalProps) {
  const toast = useToast();
  const [name, setName] = useState(currentName);
  const [updateSubscriptionName, { loading }] = useUpdateSubscriptionNameV2Mutation();

  useEffect(() => {
    setName(currentName);
  }, [currentName, isOpen]);

  const handleSave = async () => {
    try {
      await updateSubscriptionName({
        variables: {
          input: {
            subscriptionId,
            name,
          },
        },
      });

      toast({
        status: 'success',
        title: t('subscriptionNameUpdated'),
      });

      onNameUpdated();
      onClose();
    } catch (error) {
      toast({
        status: 'error',
        title: t('updateSubscriptionNameErr'),
        description: fromError(error, 'UpdateSubscriptionName', {}),
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('editSubscriptionName')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <Text mb="2" fontWeight="medium">
              {t('subscriptionName')}
            </Text>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('subscriptionName')}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr="3" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={loading}
            isDisabled={!name.trim() || name === currentName}
          >
            {t('save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

type SubscriptionDetailManageV2Props = {
  subscription: SubscriptionFragment;
};

export function SubscriptionDetailManageV2({ subscription }: SubscriptionDetailManageV2Props) {
  const { isEnabled: isAppDirectMarketplaceLinkEnabled } = useFeatureFlag(
    'appDirectMarketplaceLink',
  );

  return (
    <Box>
      <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
        {t('manageSubscription')}
      </Heading>
      <Text color="gray.500">
        {t('upgradeSubscriptionSupport')}
        <br />
        {t('upgradeSubscriptionSupportMsg')} <strong>&quot;{subscription.productId}&quot;</strong>.
      </Text>
      {isAppDirectMarketplaceLinkEnabled && (
        <EmptyViewExternalLink
          href={process.env.REACT_APP_APPDIRECT_MARKETPLACE_LINK}
          label={t('manageSubscription')}
        />
      )}
    </Box>
  );
}

type EditSubscriptionFormV2Props = {
  subscription: SubscriptionFragment;
  customers: CustomerFragment[];
  form: UseFormReturn<FormValues>;
};

export function EditSubscriptionFormV2({
  subscription,
  form,
  customers,
}: EditSubscriptionFormV2Props) {
  const subscriptionCustomerIds = useMemo(
    () => subscription.customers.map(({ id }) => id),
    [subscription.customers],
  );
  const table = useSubscriptionCustomersTableV2(
    customers,
    subscriptionCustomerIds,
    subscription.status === 'ACTIVE',
  );

  const {
    formState: { isSubmitSuccessful },
    setValue,
    reset,
  } = form;

  // When subscription customer selection changes, update the form data
  useEffect(() => {
    const checkedCustomerIds = Object.keys(table.state.selectedRowIds).sort();
    const sortedSubscriptionCustomerIds = [...subscriptionCustomerIds].sort();

    const hasNumberOfCustomersChanged =
      checkedCustomerIds.length !== sortedSubscriptionCustomerIds.length;
    const isEverySubscriptionCustomersStillChecked = sortedSubscriptionCustomerIds.every((id) =>
      checkedCustomerIds.includes(id),
    );

    const selectionChangeUpdatedData =
      hasNumberOfCustomersChanged || !isEverySubscriptionCustomersStillChecked;

    setValue('customerIds', checkedCustomerIds, {
      shouldTouch: true,
      shouldDirty: selectionChangeUpdatedData,
    });

    // Un-dirty the form to disable the submit button again when there are no changes compared to the starting data
    if (selectionChangeUpdatedData === false) {
      reset(undefined, { keepValues: true, keepDirty: false, keepDefaultValues: false });
    }
  }, [setValue, subscriptionCustomerIds, table.state.selectedRowIds, reset]);

  // Reset the form after as successful submit
  useEffect(() => {
    if (isSubmitSuccessful) {
      // Un-dirtying needed to disable the submit button again
      reset(undefined, { keepValues: true, keepDirty: false, keepDefaultValues: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `table` changes are of no interest
  }, [isSubmitSuccessful, reset]);

  return (
    <form>
      <VStack alignItems="flex-start" spacing={10}>
        <FormControl>
          <Heading borderBottom="1px solid" borderColor="gray.50" fontSize="1.5rem" mb="8" pb="2">
            {t('customers')}
          </Heading>
          {isNil(customers) || isEmpty(customers) ? (
            <>{t('noCustomers')}</>
          ) : (
            <SubscriptionCustomersTableV2
              table={table}
              currentCustomerIds={subscriptionCustomerIds}
              isActive={subscription.status === 'ACTIVE'}
            />
          )}
        </FormControl>
      </VStack>
    </form>
  );
}

OrganizationSubscriptionEditPageV2.graphql = {
  fragments: {
    SubscriptionEditPageV2_subscription: gql`
      fragment SubscriptionEditPageV2_subscription on WaveSubscription {
        id
        name
        userGivenName
        usage {
          current
          max
        }
        validUntil
        productId
        assignable
        customers {
          id
        }
        status
      }
    `,
  },
  queries: {
    SubscriptionEditPageV2Organization: gql`
      query WaveSubscriptionEditPageV2Organization {
        organization {
          customers {
            ...SubscriptionCustomersTableV2Customer
          }
          waveSubscriptions {
            ...SubscriptionEditPageV2_subscription
          }
        }
      }
    `,
  },
  mutations: {
    EditSubscriptionV2: gql`
      mutation EditSubscriptionV2($input: SubscriptionUpdateInput!) {
        updateSubscription(input: $input) {
          id
          ...SubscriptionEditPageV2_subscription
        }
      }
    `,
    UpdateSubscriptionNameV2: gql`
      mutation UpdateSubscriptionNameV2($input: SubscriptionNameUpdateInput!) {
        updateSubscriptionName(input: $input) {
          id
          name
          userGivenName
        }
      }
    `,
  },
};
